<template>
  <vue-tabs ref="tabs" v-model="activeTab">
    <v-tab :title="t('superuserGiftcardEditHeader')">
      <template v-if="selectedGiftCardData">
        <validation-observer
          ref="giftCardDataForm"
          v-element-loader="{ endpoint: createEndpoint() }"
          tag="div"
          class="superuser-gift-card__tab-container">
          <div class="superuser-gift-card__block superuser-gift-card__block--border">
            <div class="superuser-gift-card__block-content">
              <oie-base-input
                v-model="code"
                :label-text="t('superuserGiftcardColumnCode')"
                :is-readonly="true"
                class="superuser-gift-card__input-code"></oie-base-input>

              <oie-base-input
                v-model="amountWithCurrency"
                :label-text="t('superuserGiftcardColumnOriginalValue')"
                :is-readonly="true"
                class="superuser-gift-card__input-narrow"></oie-base-input>

              <oie-base-input
                v-model="balanceWithCurrency"
                :label-text="t('superuserGiftcardColumnRemainingAmount')"
                :is-readonly="true"
                class="superuser-gift-card__input-narrow"></oie-base-input>

              <oie-base-input
                v-model="selectedGiftCardData.validUntil"
                :label-text="t('superuserGiftcardColumnValidUntil')"
                :is-readonly="true"
                class="superuser-gift-card__input-narrow"></oie-base-input>

              <oie-base-input
                v-model="selectedGiftCardData.customerNumber"
                :label-text="t('superuserGiftcardColumnCustomerNumber')"
                :is-readonly="true"
                class="superuser-gift-card__input-narrow"></oie-base-input>

              <oie-base-input
                v-model="selectedGiftCardData.orderNumber"
                :label-text="t('superuserGiftcardColumnOrderNumber')"
                :is-readonly="true"
                class="superuser-gift-card__input"></oie-base-input>

              <oie-base-input
                v-model="selectedGiftCardData.emailSentAt"
                :label-text="t('superuserGiftcardColumnEmailSentAt')"
                :is-readonly="true"
                class="superuser-gift-card__input"></oie-base-input>

              <oie-base-input
                v-model="selectedGiftCardData.recipientEmail"
                :label-text="t('superuserGiftcardColumnRecipientEmail')"
                :is-readonly="true"
                class="superuser-gift-card__input-wide"></oie-base-input>

              <oie-base-input
                v-model="selectedGiftCardData.readAt"
                :label-text="t('superuserGiftcardColumnReadAt')"
                :is-readonly="true"
                class="superuser-gift-card__input"></oie-base-input>

              <oie-base-input
                v-model="selectedGiftCardData.id"
                :label-text="t('superuserGiftcardColumnGiftCardId')"
                :is-readonly="true"
                class="superuser-gift-card__input-narrow"></oie-base-input>
            </div>
          </div>

          <div v-if="selectedGiftCardData.cancelledAt" class="superuser-gift-card__block superuser-gift-card__block--border">
            <h2 class="superuser-gift-card__block-header superuser-gift-card__block-header-cancelled">
              {{ t('superuserGiftcardCanceledHeader') }}
            </h2>

            <div class="superuser-gift-card__block-content">
              <oie-base-input
                v-model="selectedGiftCardData.cancelledAt"
                :label-text="t('superuserGiftcardColumnCancelledAt')"
                :is-readonly="true"
                class="superuser-gift-card__input"></oie-base-input>

              <oie-base-input
                v-model="selectedGiftCardData.cancelledBy"
                :label-text="t('superuserGiftcardColumnCancelledBy')"
                :is-readonly="true"
                class="superuser-gift-card__input"></oie-base-input>

              <oie-base-input
                v-model="selectedGiftCardData.cancelledReason"
                :label-text="t('superuserGiftcardColumnCancelledReason')"
                :textarea="true"
                :is-readonly="true"
                class="superuser-gift-card__input"></oie-base-input>
            </div>
          </div>

          <div class="superuser-gift-card__block superuser-gift-card__block--border">
            <h2 class="superuser-gift-card__block-header">
              {{ t('superuserGiftcardTransactionsHeader') }}
            </h2>

            <div class="superuser-transactions">
              <div v-if="mq('medium')" class="superuser-transactions__head">
                <div class="superuser-transactions__cell">
                  {{ t('superuserGiftcardTransColumnDate') }}
                </div>

                <div class="superuser-transactions__cell">
                  {{ t('superuserGiftcardTransColumnOrderNumber') }}
                </div>

                <div class="superuser-transactions__cell">
                  {{ t('superuserGiftcardTransColumnAmount') }}
                </div>

                <div class="superuser-transactions__cell">
                  {{ t('superuserGiftcardTransColumnReason') }}
                </div>
              </div>

              <div
                v-for="(row) in selectedGiftCardData.transactions"
                :key="'hist' + row.transactionID"
                :class="'superuser-transactions__body-row'">
                <div class="superuser-transactions__cell">
                  {{ row.createdAt }}
                </div>

                <div class="superuser-transactions__cell">
                  {{ row.orderNumber }}
                </div>

                <div :class="'superuser-transactions__cell ' + (row.amount < 0 ? 'superuser-transactions__withdrawal' : 'superuser-transactions__deposit')">
                  {{ row.amount }}
                </div>

                <div class="superuser-transactions__cell">
                  {{ row.reason }}
                </div>
              </div>
            </div>
          </div>
        </validation-observer>

        <div class="superuser-gift-card__action-block">
          <oie-base-button
            class="button--true-ghost"
            :text="t('superuserGiftcardBack')"
            @click="cancelEdit()"></oie-base-button>

          <oie-base-button
            v-if="!selectedGiftCardData.cancelledAt"
            class="button--with-border button--red"
            :text="t('superuserGiftcardCancel')"
            @click="cancelGiftCard()"></oie-base-button>
        </div>
      </template>
    </v-tab>

    <v-tab :title="t('superuserGiftcardTabResend')">
      <template>
        <validation-observer
          ref="resendGiftCardForm"
          tag="div"
          class="superuser-gift-card__tab-container">
          <div class="superuser-gift-card__block">
            <h2 class="superuser-gift-card__block-header">
              <span class="superuser-gift-card__block-header-sub">
                {{ t('superuserGiftcardResendInstruction') }}
              </span>
            </h2>
            <div class="superuser-gift-card__block-content">
              <validation-provider
                v-slot="{errors}"
                rules="required|email"
                slim>
                <oie-base-input
                  id="superuser-gift-card-email"
                  v-model="resendToEmail"
                  :label-text="t('superuserGiftcardResendEmail') + '*'"
                  :error-messages="errors"
                  :use-gray-input="true"
                  class="superuser-gift-card__input-wide"></oie-base-input>
              </validation-provider>
            </div>
            <div :class="resendResult ? 'superuser-gift-card__resend-success' : 'superuser-gift-card__resend-failed'">
              {{ resendResultMessage }}
            </div>
          </div>

          <!-- Resend history log -->
          <template>
            <div class="superuser-gift-card__block superuser-gift-card__block--border">
              <h2 class="superuser-gift-card__block-header-history" @click="toggleTable">
                {{ t('superuserGiftcardResendHistory') }}
                <span class="arrow" :class="{'arrow-up': isTableExpanded, 'arrow-down': !isTableExpanded}"></span>
              </h2>

              <div v-if="isTableExpanded" class="superuser-resend-history">
                <div v-if="mq('medium')" class="superuser-resend-history__head">
                  <div class="superuser-resend-history__cell">
                    {{ t('superuserGiftcardResendColumnDate') }}
                  </div>
                  <div class="superuser-resend-history__cell">
                    {{ t('superuserGiftcardResendColumnSentBy') }}
                  </div>
                  <!-- <div class="superuser-resend-history__cell">
                    Event
                  </div> -->
                  <div class="superuser-resend-history__cell">
                    {{ t('superuserGiftcardResendColumnSentTo') }}
                  </div>
                </div>

                <div
                  v-for="(row) in resendHistory"
                  :key="'hist' + row.startDate"
                  :class="'superuser-resend-history__body-row'">
                  <div class="superuser-resend-history__cell">
                    {{ row.startDate }}
                  </div>

                  <div class="superuser-resend-history__cell">
                    {{ row.user }}
                  </div>

                  <!-- <div class="superuser-resend-history__cell">
                    {{ row.eventType }}
                  </div> -->

                  <div class="superuser-resend-history__cell">
                    {{ row.sentToRecipientEmail }}
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- Click history -->
          <template>
            <div class="superuser-gift-card__block superuser-gift-card__block--border">
              <h2 class="superuser-gift-card__block-header-history" @click="toggleTableClick">
                {{ t('superuserGiftcardClickHistory') }}
                <span class="arrow" :class="{'arrow-up': isTableClickExpanded, 'arrow-down': !isTableClickExpanded}"></span>
              </h2>

              <div v-if="isTableClickExpanded" class="superuser-resend-history">
                <div v-if="mq('medium')" class="superuser-resend-history__head">
                  <div class="superuser-resend-history__cell">
                    {{ t('superuserGiftcardClickColumnDate') }}
                  </div>

                  <div class="superuser-resend-history__cell">
                    {{ t('superuserGiftcardClickColumnOpenedBy') }}
                  </div>
                </div>

                <div
                  v-for="(row) in clickHistory"
                  :key="'histClick' + row.startDate"
                  :class="'superuser-resend-history__body-row'">
                  <div class="superuser-resend-history__cell">
                    {{ row.startDate }}
                  </div>

                  <div class="superuser-resend-history__cell">
                    {{ row.email }}
                  </div>
                </div>
              </div>
            </div>
          </template>

          <div class="superuser-gift-card__action-block">
            <oie-base-button class="button--true-ghost" :text="t('superuserCancelButtonLabel')" @click="cancelEdit()"></oie-base-button>
            <oie-base-button class="button--with-border button--blue" text="Skicka presentkort" @click="resendGiftCard()"></oie-base-button>
          </div>
        </validation-observer>
      </template>
    </v-tab>

    <v-tab :title="t('superuserGiftcardTabChangeValidUntil')">
      <template>
        <validation-observer
          ref="resendGiftCardForm"
          tag="div"
          class="superuser-gift-card__tab-container">
          <div class="superuser-gift-card__block">
            <h2 class="superuser-gift-card__block-header">
              <span class="superuser-gift-card__block-header-sub">
                {{ t('superuserGiftcardChangeValidUntilInstruction') }} <b>{{ selectedGiftCardData.validUntil }}</b>
              </span>
            </h2>
            <div class="superuser-gift-card__block-content">
              <datepicker
                ref="newValidUntil"
                v-model="newValidUntil"
                :monday-first="true"
                wrapper-class="datepicker"
                calendar-class="datepicker-calendar"
                input-class="datepicker-input"
                format="yyyy-MM-dd"
                :language="language"
                :full-month-name="true"
                :editable="true"
                :disabled="false"
                :typeable="true"
                @input="onDateInput"></datepicker>
            </div>
            <div v-if="errorMessage" class="error-message">
              {{ errorMessage }}
            </div>
            <div v-if="warningMessage" class="warning-message">
              {{ warningMessage }}
            </div>
            <!-- <div :class="validUntilResult ? 'superuser-gift-card__resend-success' : 'superuser-gift-card__resend-failed'">
              {{ validUntilResultMessage }}
            </div> -->
          </div>

          <!-- Valid until history -->
          <template>
            <div class="superuser-gift-card__block superuser-gift-card__block--border">
              <h2 class="superuser-gift-card__block-header-history">
                {{ t('superuserGiftcardValidUntilHistory') }}
              </h2>

              <div class="superuser-valid-history">
                <div v-if="mq('medium')" class="superuser-valid-history__head">
                  <div class="superuser-valid-history__cell">
                    {{ t('superuserGiftcardValidUntilColumnDate') }}
                  </div>

                  <div class="superuser-valid-history__cell">
                    {{ t('superuserGiftcardValidUntilColumnChangedBy') }}
                  </div>

                  <div class="superuser-valid-history__cell">
                    {{ t('superuserGiftcardValidUntilColumnOldValue') }}
                  </div>

                  <div class="superuser-valid-history__cell">
                    {{ t('superuserGiftcardValidUntilColumnNewValue') }}
                  </div>
                </div>

                <div
                  v-for="(row) in selectedGiftCardData.validHistory"
                  :key="'histValid' + row.startDate"
                  :class="'superuser-valid-history__body-row'">
                  <div class="superuser-valid-history__cell">
                    {{ row.startDate }}
                  </div>

                  <div class="superuser-valid-history__cell">
                    {{ row.userName }}
                  </div>

                  <div class="superuser-valid-history__cell">
                    {{ row.oldDate }}
                  </div>

                  <div class="superuser-valid-history__cell">
                    {{ row.newDate }}
                  </div>
                </div>
              </div>
            </div>
          </template>

          <div class="superuser-gift-card__action-block">
            <oie-base-button class="button--true-ghost" :text="t('superuserCancelButtonLabel')" @click="cancelEdit()"></oie-base-button>
            <oie-base-button
              :disabled="!isDateValid"
              class="button--with-border button--blue"
              :text="t('superuserGiftcardChangeValidUntilButton')"
              @click="changeValidUntilOnGiftCard()"></oie-base-button>
          </div>
        </validation-observer>
      </template>
    </v-tab>
  </vue-tabs>
</template>

<script>
import { sv } from 'vuejs-datepicker/dist/locale'
import { VueTabs, VTab } from 'vue-nav-tabs/dist/vue-tabs.js'
import httpService from '@/core/http-service'
import Datepicker from 'vuejs-datepicker/dist/vuejs-datepicker.esm.js'
import _ from 'lodash'

export default {
  components: {
    Datepicker,
    VueTabs,
    VTab
  },
  props: {
    selectedGiftCard: {
      type: Object,
      required: true
    },
    selectedGiftCardIndex: {
      type: Number,
      required: true
    }
  },
  data: () => ({
    newValidUntil: null,
    activeTab: null,
    selectedGiftCardDataOrigin: null,
    selectedGiftCardData: null,
    resendToEmail: null,
    resendResult: null,
    resendResultMessage: null,
    language: sv,
    validUntilResult: null,
    validUntilResultMessage: null,
    errorMessage: '', // Initialize error message
    warningMessage: '', // Initialize warning message
    resendHistory: null,
    clickHistory: null,
    isTableExpanded: false, // Track expanded state of the table
    isTableClickExpanded: false // Track expanded state of the table
  }),
  computed: {
    isDateValid() {
      return this.newValidUntil !== null && this.errorMessage === '' // Check if date is valid
    },
    tabsRef() {
      return this.$refs.tabs
    },
    isCustomerDataTabActive() {
      return this.tabsRef.activeTabIndex === 0
    },
    code() {
      return `****-****-****-${this.selectedGiftCardData.code}`
    },
    amountWithCurrency() {
      return `${this.selectedGiftCardData.amount} ${this.selectedGiftCardData.currency}`
    },
    balanceWithCurrency() {
      return `${this.selectedGiftCardData.balance} ${this.selectedGiftCardData.currency}`
    }
  },
  watch: {
    activeTab: {
      handler(newVal) {
        this.resendResult = null
        this.resendResultMessage = null
      }
    },
    selectedGiftCard: {
      handler(newVal) {
        this.freezeCopySelectedGiftCard()
        httpService.get(this.createEndpoint('details')).then((data) => {
          this.selectedGiftCardData = data.data
          this.resendToEmail = data.data.recipientEmail
          this.newValidUntil = data.data.validUntil
        })
        this.resendResult = null
        this.resendResultMessage = null
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
  },
  methods: {
    onDateInput(value) {
      if (value instanceof Date) {
        this.newValidUntil = value // Set newValidUntil to the Date object
        this.errorMessage = '' // Clear error message
        this.checkDateWarning(value) // Check for warning
      } else {
        // Validate the entered date string
        if (this.isValidDate(value)) {
          const dateObject = new Date(value) // Create a Date object
          this.newValidUntil = dateObject // Create a Date object
          this.errorMessage = '' // Clear error message
          this.checkDateWarning(dateObject) // Check for warning
        } else {
          this.errorMessage = 'Felaktigt datum format. Använd yyyy-MM-dd.' // Set error message
          this.newValidUntil = this.selectedGiftCardData.validUntil // set origin value
          this.warningMessage = '' // Clear warning message
        }
      }
    },
    checkDateWarning(selectedDate) {
      // const currentDate = this.selectedGiftCardData.validUntil // Get the current date
      // Check if the selected date is earlier than the current date
      if (this.formatDate(selectedDate) < this.selectedGiftCardData.validUntil) {
        this.warningMessage = 'The valda datumet är tidigare än nuvarande datum.' // Set warning message
      } else {
        this.warningMessage = '' // Clear warning message if date is valid
      }
    },
    isValidDate(dateString) {
      // Check if the date is in the format yyyy-MM-dd
      const regex = /^\d{4}-\d{2}-\d{2}$/
      if (!regex.test(dateString)) return false

      const date = new Date(dateString)
      // Check if the date is valid and matches the input string
      return date instanceof Date && !isNaN(date) && dateString === this.formatDate(date)
    },
    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-based
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}` // Format as yyyy-MM-dd
    },
    toggleTable() {
      this.isTableExpanded = !this.isTableExpanded
      if(this.isTableExpanded) {
        httpService.get(this.createEndpoint('resend/history')).then((data) => {
          this.resendHistory = data.data.history
          // console.log(this.resendHistory)
        })
      }
    },
    toggleTableClick() {
      this.isTableClickExpanded = !this.isTableClickExpanded
      if(this.isTableClickExpanded) {
        httpService.get(this.createEndpoint('clicked/history')).then((data) => {
          this.clickHistory = data.data.history
          // console.log(this.clickHistory)
        })
      }
    },
    createEndpoint(action) {
      let endpoint = `superuser/giftcard/${this.selectedGiftCard.id}`
      if (action !== undefined) {
        endpoint += `/${action}`
      }
      return endpoint
    },
    resetModels() {
      this.selectedGiftCardId = null
    },
    resetSelectedGiftCardData() {
      this.publish('superuser-reset-gift-card-data', { originalValues: this.selectedGiftCardOrigin, index: this.selectedGiftCardIndex })
    },
    updatedSelecteGiftCardData(giftCard) {
      this.publish('superuser-gift-card-updated', { giftCard: giftCard, index: this.selectedGiftCardIndex })
    },
    updateUserAndReset(giftCard) {
      this.publish('superuser-update-and-reset-user-data', { giftCard: giftCard })
    },
    freezeCopySelectedGiftCard() {
      this.selectedGiftCardDataOrigin = _.cloneDeep(this.selectedGiftCardData)
    },
    cancelEdit() {
      this.resetSelectedGiftCardData()
      this.resetModels()
    },
    cancelGiftCard() {
      this.triggerModalCancelGiftCard()
    },
    changeValidUntilOnGiftCard() {
      httpService.post(this.createEndpoint('updateValidUntil'), { currentDate: this.selectedGiftCardData.validUntil, selectedDate: this.newValidUntil }).then((data) => {
        this.validUntilResult = data.data.success
        this.validUntilResultMessage = data.data.message
        this.selectedGiftCardData = data.data.giftCard
      })
    },
    resendGiftCard() {
      this.$refs.resendGiftCardForm.validate().then(success => {
        if (success) {
          httpService.post(this.createEndpoint('resend'), { email: this.resendToEmail }).then((data) => {
            this.resendResult = data.data.success
            this.resendResultMessage = data.data.message
            this.selectedGiftCardData = data.data.giftCard
            this.isTableExpanded = false
            this.isTableClickExpanded = false
          })
        }
      })
    },
    triggerModalCancelGiftCard() {
      this.publish('modal.show.gift-card-cancel-superuser', {
        header: 'Makulera presentkort',
        text: `****-****-****-${this.selectedGiftCardData.code}`,
        reasonLabel: 'Makuleringsorsak',
        handler: (cancelledReason) => {
          httpService.post(this.createEndpoint('cancel'), { cancelledReason }).then((data) => {
            this.selectedGiftCardData = data.data
            this.$modal.hide('gift-card-cancel-superuser')
          })
        }
      })
    }
  }
}
</script>

<style lang="scss">
.superuser-valid-history {
  @include body-smaller;
  background-color: color(background-white);
  border: 1px solid color(border-gray);
  @include media(medium) {
    width: 80%
  }
}

.superuser-valid-history > div:not(:last-child) {
  border-bottom: 1px solid color(border-gray);
}

.superuser-valid-history__head {
  display: flex;
  background-color: color(background-blue-6);
  padding-right: measure(quarter);
  padding-left: measure(quarter);
}

.superuser-valid-history__body-row {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  flex-wrap: wrap;
  padding-right: measure(quarter);
  padding-left: measure(quarter);

  @include media(small) {
    flex-direction: row;
  }

  @include media(medium) {
    flex-wrap: nowrap;
  }
}

.superuser-valid-history__cell {
  flex-shrink: 0;
  padding: measure(quarter);

  @include media(to-small) {
    width: 100%;
  }

   &:nth-child(1) {
    @include media(small) {
      width: 50%;
    }

    @include media(medium) {
      width: 25%;
    }
  }

   &:nth-child(2) {
    @include media(small) {
      width: 50%;
    }

    @include media(medium) {
      width: 25%;
      word-break: break-all;
    }
  }

  &:nth-child(3) {
    @include media(small) {
      width: 50%;
    }

    @include media(medium) {
      width: 25%;
      word-break: break-all;
    }
  }
}

.error-message {
 color: red; /* Style for error message */
}
.warning-message {
 color: orange; /* Style for warning message */
}
.superuser-gift-card__block {
  padding: measure(full) 0;

  &:first-child {
    padding-top: 0;
  }
}

.superuser-gift-card__block--border {
  border-bottom: 1px solid color(border-gray);
}

.superuser-gift-card__block--margin-bottom {
  margin-bottom: measure(double);
}

.superuser-gift-card__block-content {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: measure(half);
}

.superuser-gift-card__block-content--sync-info {
  flex-direction: column;

  @include media(small) {
    flex-direction: row;
  }
}

.superuser-gift-card__block-header-history {
 @include body-bold;
 @include media(medium) {
    width: 80%
  }
 margin-top: 0;
 cursor: pointer;
 transition: background-color 0.3s;
 padding: 5px;
}

.superuser-gift-card__block-header-history:hover {
 background-color: color(background-gray-5);
 @include media(medium) {
    width: 80%
  }
}

.arrow {
 font-size: 1.0em; /* Smaller font size for the arrow */
 margin-left: 5px;
}

.arrow-up::after {
 content: '▲'; /* Up arrow */

}

.arrow-down::after {
 content: '▼'; /* Down arrow */
}

.superuser-resend-history {
  @include body-smaller;
  background-color: color(background-white);
  border: 1px solid color(border-gray);
  @include media(medium) {
    width: 80%
  }
}

.superuser-resend-history > div:not(:last-child) {
  border-bottom: 1px solid color(border-gray);
}

.superuser-resend-history__head {
  display: flex;
  background-color: color(background-blue-6);
  padding-right: measure(quarter);
  padding-left: measure(quarter);
}

.superuser-resend-history__body-row {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  flex-wrap: wrap;
  padding-right: measure(quarter);
  padding-left: measure(quarter);

  @include media(small) {
    flex-direction: row;
  }

  @include media(medium) {
    flex-wrap: nowrap;
  }
}

.superuser-resend-history__cell {
  flex-shrink: 0;
  padding: measure(quarter);

  @include media(to-small) {
    width: 100%;
  }

   &:nth-child(1) {
    @include media(small) {
      width: 50%;
    }

    @include media(medium) {
      width: 25%;
    }
  }

   &:nth-child(2) {
    @include media(small) {
      width: 50%;
    }

    @include media(medium) {
      width: 25%;
      word-break: break-all;
    }
  }

  &:nth-child(3) {
    @include media(small) {
      width: 50%;
    }

    @include media(medium) {
      width: 25%;
      word-break: break-all;
    }
  }
}

.superuser-gift-card__block-header {
  @include body-bold;
  margin-top: 0;
}

.superuser-gift-card__block-header-sub {
  @include body;
}

.superuser-gift-card__resend-success {
  color: #090;
}

.superuser-gift-card__resend-failed {
  color: #D00;
}

.superuser-gift-card__block-header-cancelled {
  color: #D00;
}

.superuser-gift-card__input {
  flex: 1 0 11rem;
}

.superuser-gift-card__input-code {
  flex: 4 0 12rem;
  max-width: 15rem;
}

.superuser-gift-card__input-wide {
  flex: 4 0 18rem;
  max-width: 31rem;
}

.superuser-gift-card__input-narrow {
  flex: 1 0 11rem;
  max-width: 10rem;
}

.superuser-gift-card__input-checkbox {
  flex-shrink: 0;
  flex-basis: auto;
}

.superuser-gift-card__input-filler {
  flex-basis: 11rem;
}

.superuser-gift-card__action-block {
  display: flex;
  justify-content: flex-end;
  padding: measure(quarter) measure(half);
  border: 1px solid color(border-gray);
}

.superuser-gift-card__action-block > * {
  @include body-smaller-semi-bold;

  &:first-child {
    @include media(small) {
      margin-right: auto;
      padding-left: 0;
    }
  }
}

.superuser-transactions {
  @include body-smaller;
  background-color: color(background-white);
  border: 1px solid color(border-gray);
  @include media(medium) {
    width: 60%
  }
}

.superuser-transactions > div:not(:last-child) {
  border-bottom: 1px solid color(border-gray);
}

.superuser-transactions__head {
  display: flex;
  background-color: color(background-blue-6);
  padding-right: measure(quarter);
  padding-left: measure(quarter);
}

.superuser-transactions__body-row {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  flex-wrap: wrap;
  padding-right: measure(quarter);
  padding-left: measure(quarter);

  @include media(small) {
    flex-direction: row;
  }

  @include media(medium) {
    flex-wrap: nowrap;
  }
}

.superuser-transactions__cell {
  flex-shrink: 0;
  padding: measure(quarter);

  @include media(to-small) {
    width: 100%;
  }

   &:nth-child(1) {
    @include media(small) {
      width: 50%;
    }

    @include media(medium) {
      width: 25%;
    }
  }

   &:nth-child(2) {
    @include media(small) {
      width: 50%;
    }

    @include media(medium) {
      width: 25%;
      word-break: break-all;
    }
  }

  &:nth-child(3) {
    @include media(small) {
      width: 50%;
    }

    @include media(medium) {
      width: 25%;
      word-break: break-all;
    }
  }
}

.superuser-transactions__withdrawal {
  color: #D00
}

.superuser-transactions__deposit {
  color: #0a0
}

</style>
